import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../config/supabase'
import Footer from '../components/Footer'
import RankPopup from '../components/RankPopup'
import { getRankInfo } from '../utils/rankUtils'
import { Helmet } from 'react-helmet-async'

const Dashboard = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showRankPopup, setShowRankPopup] = useState(false)
  const [membership, setMembership] = useState(null)
  const [hasPurchasedVIP, setHasPurchasedVIP] = useState(null)
  const [countdown, setCountdown] = useState({ days: 7, hours: 0, minutes: 0, seconds: 0 })
  const [countdownCycle, setCountdownCycle] = useState('initial') // 'initial' = 7 วันแรก, 'repeat' = 3 วันต่อๆ ไป

  // นับเวลาถอยหลังแบบวนซ้ำ
  useEffect(() => {
    // ตรวจสอบว่ามีข้อมูลการนับถอยหลังที่บันทึกไว้หรือไม่
    const savedCountdown = localStorage.getItem('countdownData');
    let countdownDate = new Date();
    
    if (savedCountdown) {
      const parsedData = JSON.parse(savedCountdown);
      countdownDate = new Date(parsedData.targetDate);
      setCountdownCycle(parsedData.cycle);
    } else {
      // ถ้าไม่มีข้อมูลที่บันทึกไว้ ให้ตั้งค่าเริ่มต้นเป็น 7 วัน
      countdownDate.setDate(countdownDate.getDate() + 7);
      
      // บันทึกข้อมูลลง localStorage
      const countdownData = {
        targetDate: countdownDate.toISOString(),
        cycle: 'initial'
      };
      localStorage.setItem('countdownData', JSON.stringify(countdownData));
    }
    
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = countdownDate.getTime() - now;
      
      if (distance < 0) {
        // เมื่อครบกำหนด ให้เริ่มนับใหม่
        const newCountdownDate = new Date();
        newCountdownDate.setDate(newCountdownDate.getDate() + 3); // ตั้งเป็น 3 วันสำหรับรอบต่อไป
        
        // บันทึกข้อมูลลง localStorage
        const countdownData = {
          targetDate: newCountdownDate.toISOString(),
          cycle: 'repeat'
        };
        localStorage.setItem('countdownData', JSON.stringify(countdownData));
        
        // อัปเดต state
        countdownDate = newCountdownDate;
        setCountdownCycle('repeat');
        return;
      }
      
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds });
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchMembership = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('membership, has_purchased_vip')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        setMembership(profile.membership);
        setHasPurchasedVIP(profile.has_purchased_vip);
      } catch (error) {
        console.error('Error fetching membership:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMembership();
  }, [user]);

  useEffect(() => {
    if (user) {
      getProfile()
    }
  }, [user])

  const getProfile = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (error) throw error
      setProfile(data)
    } catch (error) {
      console.error('Error loading profile:', error.message)
    } finally {
      setLoading(false)
    }
  }

  const getMembershipText = (membership) => {
    switch (membership) {
      case 'free':
        return (
          <div className="flex items-center gap-2">
            <span className="text-gray-400">🆓</span>
            <span>สาชิกฟรี</span>
          </div>
        )
      case 'vip':
        return (
          <div className="flex items-center gap-2">
            <span className="text-yellow-400">👑</span>
            <span>สมาชิก VIP</span>
          </div>
        )
      case 'lifetime':
        return (
          <div className="flex items-center gap-2">
            <span className="text-green-400">♾️</span>
            <span>สมาชิกตลอดชีพ</span>
          </div>
        )
      case 'admin':
        return (
          <div className="flex items-center gap-2">
            <span className="text-red-400">⚡</span>
            <span>ผู้ดูแลระบบ</span>
          </div>
        )
      default:
        return (
          <div className="flex items-center gap-2">
            <span>❓</span>
            <span>ไม่ระบุ</span>
          </div>
        )
    }
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return null
    const now = new Date()
    const expiry = new Date(expiryDate)
    const diffTime = expiry - now
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const handleNavigation = (path) => {
    navigate(path)
  }

  const menuItems = [
    {
      title: 'สร้าง Prompt',
      description: 'สร้าง Prompt ฟรี',
      icon: (
        <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      ),
      link: '/generator',
      isVip: false,
      showWhen: membership === 'free'
    },
    {
      title: 'สร้าง Prompt VIP',
      description: 'สร้าง Prompt VIP ด้วย AI',
      icon: (
        <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      ),
      link: '/VIPGen',
      isVip: true,
      showWhen: membership !== 'free' || hasPurchasedVIP === true,
      bgColor: 'bg-blue-500/10',
      hoverBgColor: 'hover:bg-blue-500/20',
      borderColor: 'border-blue-500/20',
      vipTagColor: 'from-blue-600 to-cyan-600',
      titleGradient: 'from-blue-400 to-cyan-400'
    },
    {
      title: 'Ready Prompt',
      description: 'สร้าง Prompt VIP + AI',
      icon: (
        <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      ),
      link: membership === 'free' && !hasPurchasedVIP ? '/vip' : '/ready-prompt',
      isVip: true,
      showWhen: true,
      bgColor: 'bg-yellow-500/10',
      hoverBgColor: 'hover:bg-yellow-500/20',
      borderColor: 'border-yellow-500/20',
      vipTagColor: 'from-yellow-600 to-orange-600',
      titleGradient: 'from-yellow-400 to-orange-400'
    },
    {
      title: 'บทเรียนและเทคนิค',
      description: 'คู่มือ 0-100 สำหรับมือใหม่',
      icon: (
        <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      link: '/tutorial',
      isVip: true,
      showWhen: true,
      bgColor: 'bg-green-500/10',
      hoverBgColor: 'hover:bg-green-500/20',
      borderColor: 'border-green-500/20',
      vipTagColor: 'from-green-600 to-emerald-600',
      titleGradient: 'from-green-400 to-emerald-400'
    },
    {
      title: 'คลังคีย์เวิร์ด',
      description: 'คลังคีย์เวิร์ดสำหรับสร้าง Prompt',
      icon: (
        <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      link: '/conceptKeyword',
      isVip: true,
      showWhen: true,
      bgColor: 'bg-green-500/10',
      hoverBgColor: 'hover:bg-green-500/20',
      borderColor: 'border-green-500/20',
      vipTagColor: 'from-green-600 to-emerald-600',
      titleGradient: 'from-green-400 to-emerald-400'
    },
    {
      title: 'Coming Soon',
      description: 'Coming Soon',
      icon: (
        <svg className="w-8 h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      link: '#',
      isVip: true,
      showWhen: true,
      bgColor: 'bg-purple-500/10',
      hoverBgColor: 'hover:bg-purple-500/20',
      borderColor: 'border-purple-500/20',
      vipTagColor: 'from-purple-600 to-pink-600',
      titleGradient: 'from-purple-400 to-pink-400',
      comingSoon: true
    },
    {
      title: 'เครื่องมือต่างๆ',
      description: 'เครื่องมือช่วยสร้าง Prompt',
      icon: (
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      link: membership === 'free' && !hasPurchasedVIP ? '/vip' : '/tools',
      isVip: true,
      showWhen: true,
      bgColor: 'bg-gradient-to-br from-red-500/20 via-red-600/10 to-red-700/20',
      hoverBgColor: 'hover:bg-red-500/30',
      borderColor: 'border border-red-500/30',
      titleGradient: 'from-red-400 to-rose-400',
      highlight: true,
      vipTagColor: 'from-red-600 to-rose-600'
    },
    {
      title: 'ข้อมูลสมาชิก',
      description: 'จัดการข้อมูลสมาชิกของคุณ',
      icon: (
        <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      link: '/profile',
      showWhen: true
    },
    {
      title: 'ซัพพอท',
      description: 'แชทสอบถามแอดมิน',
      icon: (
        <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      link: 'https://www.facebook.com/aiunlockedvip',
      isVip: false,
      showWhen: true
    }
  ]

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>Dashboard</title>
      </Helmet>
      <main className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 py-12">          {/* ลบส่วน VIP CTA ออกทั้งหมด */}
          {/* เริ่มแสดง content ถัดไปได้เลย */}
          
          {/* Menu Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {menuItems
              .filter(item => item.showWhen)
              .map((item, index) => (
                <div
                  key={index}
                  className={`relative p-6 rounded-2xl ${
                    item.highlight
                      ? `${item.bgColor} ${item.hoverBgColor} ${item.borderColor} shadow-lg shadow-blue-500/30 scale-105 transform`
                      : item.isVip 
                        ? item.bgColor && item.hoverBgColor 
                          ? `${item.bgColor} ${item.hoverBgColor} ${item.borderColor} shadow-lg shadow-yellow-500/20`
                          : 'bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-purple-900/40 shadow-lg shadow-purple-500/20 border border-purple-500/20'
                        : 'bg-gray-900/50 hover:bg-gray-900/70'
                  } transition-all duration-300`}
                >
                  {item.isVip && !item.highlight && (
                    <div className={`absolute -top-2 -right-2 bg-gradient-to-r ${
                      item.vipTagColor ? item.vipTagColor : 'from-purple-600 to-pink-600'
                    } text-white px-2 py-1 rounded-lg text-sm font-semibold shadow-lg`}>
                      VIP
                    </div>
                  )}
                  {item.highlight && (
                    <div className="absolute -top-2 -right-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-3 py-1 rounded-lg text-sm font-semibold shadow-lg animate-pulse">
                      แนะนำ
                    </div>
                  )}
                  {item.comingSoon && (
                    <div className="absolute -top-2 -left-2 bg-gradient-to-r from-yellow-500 to-amber-500 text-white px-2 py-1 rounded-lg text-sm font-semibold shadow-lg">
                      Coming Soon
                    </div>
                  )}
                  {item.isExternal ? (
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="block">
                      <div className="flex flex-col h-full">
                        <div className="mb-4">{item.icon}</div>
                        <h3 className={`text-xl font-semibold mb-2 ${
                          item.highlight || item.isVip 
                            ? `text-transparent bg-clip-text bg-gradient-to-r ${
                                item.titleGradient ? item.titleGradient : 'from-purple-400 to-pink-400'
                              }`
                            : ''
                        }`}>
                          {item.title}
                        </h3>
                        <p className="text-gray-400">{item.description}</p>
                      </div>
                    </a>
                  ) : (
                    <Link to={item.link || '#'} className="block">
                      <div className="flex flex-col h-full">
                        <div className="mb-4">{item.icon}</div>
                        <h3 className={`text-xl font-semibold mb-2 ${
                          item.highlight || item.isVip 
                            ? `text-transparent bg-clip-text bg-gradient-to-r ${
                                item.titleGradient ? item.titleGradient : 'from-purple-400 to-pink-400'
                              }`
                            : ''
                        }`}>
                          {item.title}
                        </h3>
                        <p className="text-gray-400">{item.description}</p>
                      </div>
                    </Link>
                  )}
                </div>
              ))}
          </div>
        </div>
      </main>

      {/* New Section with Text and Button */}
      <div className="text-center py-4 bg-gradient-to-r from-blue-500 to-purple-600">
        <h2 className="text-xl font-bold mb-2 text-white">ค้นหาคีย์เวิร์ดด้วย Custom GPT by AI Unlock</h2>
        <a 
          href="https://discord.com/invite/MxgKZkydXJ" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-block px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg font-medium transition-colors"
        >
          🚀 สมัคร VIP อย่าลืมเข้าห้อง Discord
        </a>
      </div>

      <Footer />

      <RankPopup 
        isOpen={showRankPopup}
        onClose={() => setShowRankPopup(false)}
        currentRank={getRankInfo(profile?.prompt_count).title}
        currentCount={profile?.prompt_count || 0}
      />
    </div>
  )
}

export default Dashboard